import type { Components, Theme } from '@mui/material/styles';
import { alpha } from '@mui/material/styles';
import { elevation, radius } from '../palette';
import { target, touch } from './base';

const press = { '&:active': { transform: 'scale(0.98)' } };

export const controls: Components<Theme> = {
  // The ripple is off, so keyboard focus needs its own mark; ButtonBase resets outline, hence set here.
  MuiButtonBase: {
    defaultProps: { disableRipple: true },
    styleOverrides: { root: ({ theme: t }) => ({ '&.Mui-focusVisible': { outline: `2px solid ${t.palette.secondary.main}`, outlineOffset: 2 } }) },
  },
  MuiButton: {
    defaultProps: { disableElevation: true, size: 'small' },
    styleOverrides: {
      root: { borderRadius: radius.control, paddingInline: 14, fontWeight: 500, ...target, ...press },
      sizeLarge: { paddingBlock: 10, fontSize: '0.9375rem' },
      contained: ({ ownerState, theme: t }) => ({
        boxShadow: 'none', '&.Mui-disabled': { backgroundColor: t.palette.action.selected },
        '&:hover': { boxShadow: 'none', ...(ownerState.color === 'primary' && { backgroundColor: t.palette.primary.light }) },
      }),
      outlined: ({ ownerState, theme: t }) => {
        const tone = ownerState.color && !['primary', 'inherit'].includes(ownerState.color) ? t.palette[ownerState.color as 'error'] : null;
        return {
          backgroundColor: t.palette.background.paper,
          borderColor: tone ? alpha(tone.main, 0.35) : t.palette.divider, color: tone ? tone.dark : t.palette.text.primary,
          '&:hover': { borderColor: tone ? tone.main : t.palette.text.disabled, backgroundColor: tone ? alpha(tone.main, 0.05) : t.palette.action.hover },
        };
      },
      text: ({ theme: t }) => ({ '&:hover': { backgroundColor: t.palette.action.hover } }),
    },
  },
  MuiIconButton: {
    styleOverrides: { root: ({ theme: t }) => ({ borderRadius: radius.control, ...press, '&:hover': { backgroundColor: t.palette.action.hover }, [touch]: { minWidth: 44, minHeight: 44 } }) },
  },
  MuiToggleButtonGroup: {
    styleOverrides: { root: ({ theme: t }) => ({ backgroundColor: t.palette.action.selected, borderRadius: radius.control, padding: 3, gap: 2 }) },
  },
  MuiToggleButton: {
    styleOverrides: {
      root: ({ theme: t }) => ({
        border: 0, borderRadius: radius.mark, textTransform: 'none', fontWeight: 500, color: t.palette.text.secondary, paddingBlock: 4, ...target,
        '&:hover': { backgroundColor: alpha(t.palette.background.paper, 0.6), color: t.palette.text.primary },
        '&.Mui-selected': { backgroundColor: t.palette.background.paper, color: t.palette.text.primary, boxShadow: elevation.inset, '&:hover': { backgroundColor: t.palette.background.paper } },
        '&.MuiToggleButtonGroup-grouped': { borderRadius: radius.mark, margin: 0, border: 0 },
      }),
    },
  },
  MuiTabs: {
    styleOverrides: {
      root: { minHeight: 44, [touch]: { minHeight: 48 } },
      indicator: ({ theme: t }) => ({ height: 2, backgroundColor: t.palette.text.primary }),
    },
  },
  MuiTab: {
    styleOverrides: {
      root: ({ theme: t }) => ({
        minHeight: 44, minWidth: 0, paddingInline: 4, marginRight: 24, fontWeight: 500, color: t.palette.text.secondary,
        '&:hover': { color: t.palette.text.primary }, '&.Mui-selected': { color: t.palette.text.primary }, '&.Mui-focusVisible': { outlineOffset: -2 }, [touch]: { minHeight: 48 },
      }),
    },
  },
  MuiCheckbox: { styleOverrides: { root: ({ theme: t }) => ({ color: t.palette.text.disabled, [touch]: { padding: 10 } }) } },
  MuiRadio: { styleOverrides: { root: ({ theme: t }) => ({ color: t.palette.text.disabled, [touch]: { padding: 10 } }) } },
  MuiSwitch: {
    defaultProps: { color: 'secondary' },
    styleOverrides: {
      track: ({ theme: t }) => ({ backgroundColor: t.palette.text.disabled, opacity: 1 }),
      switchBase: { '&.Mui-checked + .MuiSwitch-track': { opacity: 1 } },
      thumb: { boxShadow: elevation.inset },
    },
  },
  MuiSlider: { defaultProps: { color: 'primary' }, styleOverrides: { thumb: { boxShadow: 'none', '&:hover, &.Mui-focusVisible': { boxShadow: elevation.inset } } } },
  MuiFormControlLabel: { styleOverrides: { label: { fontSize: '0.875rem' } } },
};
